// Pure helpers for invoice generation — no DB calls, no side effects.

import type { PricingSide } from '../../../types.ts';
import { sumAmounts, sumField } from '../../../calc.ts';
import type { Pricing } from '@shared/types.ts';
import type { Transaction } from '@shared/types/transaction.ts';
import type { PreparedLineItem, CorrectionLineItem, InvoiceLinkRow, InvoiceLineRow } from '@shared/types/invoice.ts';
import type { EffectiveLineItem } from '../effectiveLineItems.ts';
import { buildProductName, buildDescription } from '../../../invoiceHelpers.ts';

// ── Correction line item builder ──────────────────────────────────────

export function buildCorrectionLineItems(
  links: InvoiceLinkRow[],
  originalLineItems: InvoiceLineRow[],
  txMap: Map<number, Transaction>,
  effectivePricing: EffectiveLineItem[] | null,
  side: PricingSide,
  language: string,
): CorrectionLineItem[] {
  return links.map((link, idx) => {
    const tx = link.transaction_id ? txMap.get(link.transaction_id) : undefined;
    const origLi = link.invoice_line_item_id
      ? originalLineItems.find(li => li.id === link.invoice_line_item_id)
      : originalLineItems[idx];

    const ordinal = origLi?.ordinal ?? idx + 1;
    const before: Pricing | undefined = effectivePricing?.find(ep => ep.ordinal === ordinal) ?? origLi;
    // `before` is undefined only when neither effectivePricing nor an original line
    // exists — a case the `tx` branch covers. Type-level cast only.
    const after: Pricing = tx ? getSidePricing(tx, side) : (before as Pricing);

    return {
      transaction_id: link.transaction_id ?? 0,
      reference_number: tx?.reference_number ?? null,
      name: origLi?.name ?? (tx ? buildProductName(tx, language, side) : 'Unknown'),
      product_id: tx?.product_id ?? null,
      gtu_code: tx?.product?.gtu_code ?? null,
      pricing: { ...after, original: before },
    };
  });
}

// ── Side pricing accessor ───────────────────────────────────────────────

export function getSidePricing(tx: Transaction, side: PricingSide): Pricing {
  const p = side === 'load' ? tx.load_pricing : tx.unload_pricing;
  if (!p) throw new Error(`Transaction ${tx.id} missing ${side}_pricing`);
  return p;
}

export function getSideCompanyId(tx: Transaction, side: PricingSide): number | null {
  return side === 'load' ? tx.load_commercial_company_id : tx.unload_commercial_company_id;
}

export function getSideCompany(tx: Transaction, side: PricingSide) {
  return side === 'load' ? tx.load_commercial_company : tx.unload_commercial_company;
}

export function getSideDate(tx: Transaction, side: PricingSide): string | null {
  return side === 'load' ? tx.load_date : tx.unload_date;
}

export function getSideIncoterms(tx: Transaction, side: PricingSide): string | null {
  return side === 'load' ? tx.load_price_incoterms : tx.unload_price_incoterms;
}

export function getSideLocationCompany(tx: Transaction, side: PricingSide) {
  return side === 'load' ? tx.load_place_company : tx.unload_place_company;
}

/** Always uses unloaded_weight — loaded_weight is deprecated for invoicing. */
export function getSideWeight(tx: Transaction, _side: PricingSide): number | null {
  return tx.unloaded_weight;
}

// ── Date helpers ────────────────────────────────────────────────────────

export function toISODate(date: Date): string {
  return date.toISOString().split('T')[0];
}

const DAP_INCOTERMS = new Set(['DAP', 'DPU', 'DDP', 'DAT', 'DDU', 'DEQ', 'DES']);

export function determineDates(
  transactions: Transaction[],
  side: PricingSide,
  ownCompanyCity: string | null | undefined,
  buyerPaymentTerms: number | null | undefined,
  dateOverrides?: { issue_date?: string; sell_date?: string; payment_to?: string; issue_place?: string },
): { issueDate: string; sellDate: string; paymentDate: string; issuePlace: string } {
  const now = toISODate(new Date());

  const firstTx = transactions[0];
  const incoterms = getSideIncoterms(firstTx, side);
  const usesUnloadDate = incoterms ? DAP_INCOTERMS.has(incoterms) : side === 'unload';

  const dates = transactions.map(tx => usesUnloadDate ? tx.unload_date : tx.load_date).filter(Boolean) as string[];
  const sortedDates = dates.sort();
  const latestDate = sortedDates.length > 0 ? sortedDates[sortedDates.length - 1] : now;

  const issueDate = dateOverrides?.issue_date || now;
  const sellDate = dateOverrides?.sell_date || latestDate;

  const paymentTerms = buyerPaymentTerms ?? 14;
  const paymentBase = new Date(issueDate);
  paymentBase.setDate(paymentBase.getDate() + paymentTerms);
  const paymentDate = dateOverrides?.payment_to || toISODate(paymentBase);

  const issuePlace = dateOverrides?.issue_place || ownCompanyCity || '';

  return { issueDate, sellDate, paymentDate, issuePlace };
}

// ── Line item builders ──────────────────────────────────────────────────

export function buildPreparedLineItems(
  transactions: Transaction[],
  side: PricingSide,
  language: string,
): PreparedLineItem[] {
  return transactions.map((tx, index) => ({
    ordinal: index + 1,
    name: buildProductName(tx, language, side),
    description: buildDescription(tx),
    gtu_code: tx.product?.gtu_code ?? null,
    transaction_id: tx.id,
    reference_number: tx.reference_number || '',
    pricing: getSidePricing(tx, side),
  }));
}

// ── Income direction ────────────────────────────────────────────────────

export function determineIncome(transactions: Transaction[], side: PricingSide): boolean {
  const pricing = getSidePricing(transactions[0], side);
  return pricing.unit_price >= 0;
}

// ── Language ────────────────────────────────────────────────────────────

export function buildInvoiceLanguage(languageCode: string): string {
  return languageCode === 'pl' ? 'pl' : `pl/${languageCode}`;
}

// ── Summary ─────────────────────────────────────────────────────────────

export function buildSummary(
  transactions: Transaction[],
  side: PricingSide,
): { transaction_count: number; total_weight: number; total_net: number; total_vat: number; total_gross: number; currency: string } {
  const pricings = transactions.map(tx => getSidePricing(tx, side));
  const amounts = sumAmounts(pricings);
  const totalWeight = sumField(
    transactions.map(tx => ({ w: getSideWeight(tx, side) ?? 0 })),
    'w',
  );
  const currency = pricings[0]?.currency ?? 'EUR';

  return {
    transaction_count: transactions.length,
    total_weight: totalWeight,
    total_net: amounts.net,
    total_vat: amounts.vat,
    total_gross: amounts.gross,
    currency,
  };
}
